import { useRef, useState } from "react";
import { audOf, dbToFrac, fracToDb, addPt, movePt, delPt, envDb } from "@/lib/clipAudio";

export const VolumeLine = ({ clip, pps, active, onChange, onSelect }) => {
  const a = audOf(clip), box = useRef(null), drag = useRef(null), lastDown = useRef(null);
  const [tip, setTip] = useState(null);
  const W = clip.len * pps;
  const line = [{ t: 0, db: envDb(a.pts, 0) }, ...a.pts, { t: clip.len, db: envDb(a.pts, clip.len) }];
  const pos = (e) => {
    const r = box.current.getBoundingClientRect();
    return { t: Math.max(0, Math.min(clip.len, (e.clientX - r.left) / pps)), db: fracToDb((e.clientY - r.top) / r.height) };
  };
  const set = (aud) => onChange({ aud });

  const addAt = (e) => {
    e.stopPropagation();
    if (!active) return onSelect();
    const p = pos(e);
    const next = addPt(a, p.t, +envDb(a.pts, p.t).toFixed(1));
    set(next);
    startDrag(e, next.pts.findIndex((x) => Math.abs(x.t - +p.t.toFixed(3)) < 0.001), next);
  };
  const startDrag = (e, i, aud = a) => {
    e.stopPropagation();
    const last = lastDown.current;
    lastDown.current = { i, ts: e.timeStamp };
    if (aud === a && last && last.i === i && e.timeStamp - last.ts < 350) { lastDown.current = null; return set(delPt(a, i)); }
    box.current.setPointerCapture(e.pointerId);
    drag.current = { i, aud };
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const p = pos(e), prev = d.aud.pts[d.i - 1], next = d.aud.pts[d.i + 1];
    const t = Math.max(prev ? prev.t + 0.05 : 0, Math.min(next ? next.t - 0.05 : clip.len, p.t));
    d.aud = movePt(d.aud, d.i, t, p.db);
    setTip({ t, db: p.db });
    set(d.aud);
  };
  const end = () => { drag.current = null; setTip(null); };

  return (
    <div ref={box} className={`absolute inset-x-0 top-4 bottom-1 ${active ? "" : "pointer-events-none"}`} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
      data-testid={`volume-line-${clip.id}`}>
      <svg className="absolute inset-0 w-full h-full overflow-visible" viewBox={`0 0 ${W} 100`} preserveAspectRatio="none">
        <line x1="0" x2={W} y1={dbToFrac(0) * 100} y2={dbToFrac(0) * 100} stroke="#ffffff22" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
        <polyline points={line.map((p) => `${p.t * pps},${dbToFrac(p.db) * 100}`).join(" ")} fill="none" stroke={a.mute ? "#ef4444" : "#FFB800"}
          strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        {active && <polyline points={line.map((p) => `${p.t * pps},${dbToFrac(p.db) * 100}`).join(" ")} fill="none" stroke="transparent" strokeWidth="12"
          vectorEffect="non-scaling-stroke" className="cursor-copy" style={{ pointerEvents: "stroke" }} onPointerDown={addAt} data-testid={`volume-line-hit-${clip.id}`} />}
      </svg>
      {active && a.pts.map((p, i) => (
        <span key={i} onPointerDown={(e) => startDrag(e, i)} onDoubleClick={(e) => { e.stopPropagation(); set(delPt(a, i)); }}
          title={`${p.db > 0 ? "+" : ""}${p.db} dB · double-click to delete`} data-testid={`volume-point-${clip.id}-${i}`}
          className="absolute w-2.5 h-2.5 -ml-[5px] -mt-[5px] rounded-full bg-[#FFB800] border border-black cursor-grab hover:scale-125 transition-transform"
          style={{ left: p.t * pps, top: `${dbToFrac(p.db) * 100}%` }} />
      ))}
      {tip && <span className="absolute -top-4 font-mono-ui text-[9px] px-1 rounded bg-black/80 text-[#FFB800] pointer-events-none" style={{ left: tip.t * pps }}
        data-testid="volume-tip">{tip.db > 0 ? "+" : ""}{tip.db} dB</span>}
    </div>
  );
};
